import { Prefs, localeFor } from './prefs';

const locale = () => localeFor(Prefs.currentLanguage());

/** Satoshis → "12.5 CW" (no trailing zeros, max 8 decimals), in the app's language. */
export function formatCW(sats: number, sign = false): string {
  const negative = sats < 0;
  const v = Math.abs(sats);
  const frac = String(v % 100_000_000).padStart(8, '0').replace(/0+$/, '');
  const nf = new Intl.NumberFormat(locale(), { maximumFractionDigits: 0 });
  const whole = nf.format(Math.floor(v / 100_000_000));
  const decimal = new Intl.NumberFormat(locale()).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.';
  const body = frac ? `${whole}${decimal}${frac}` : whole;
  const prefix = negative ? '−' : sign && sats > 0 ? '+' : '';
  return `${prefix}${body} CW`;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat(locale()).format(n);
}

export function relativeTime(unixSeconds: number): string {
  const diff = unixSeconds * 1000 - Date.now();
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto', style: 'short' });
  const abs = Math.abs(diff);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 86400e3],
    ['month', 30 * 86400e3],
    ['day', 86400e3],
    ['hour', 3600e3],
    ['minute', 60e3],
  ];
  for (const [unit, ms] of units) {
    if (abs >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  return rtf.format(0, 'minute');
}

export function formatDate(d: Date, withTime = true): string {
  return new Intl.DateTimeFormat(locale(), withTime
    ? { dateStyle: 'long', timeStyle: 'short' }
    : { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

export function isToday(d: Date): boolean {
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

export function isYesterday(d: Date): boolean {
  const y = new Date(Date.now() - 86400e3);
  return d.getFullYear() === y.getFullYear() && d.getMonth() === y.getMonth() && d.getDate() === y.getDate();
}

/** "cw1q abcd efgh …" so an address can be checked in chunks. */
export function grouped(s: string): string {
  return s.match(/.{1,4}/g)?.join(' ') ?? s;
}
